import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/app';
import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { setupForegroundNotificationListener } from '@/lib/notifications';
import { AppIcon, type AppIconName } from '@/components/AppIcon';

const navItems: Array<{ path: string; label: string; iconName: AppIconName }> = [
  { path: '/', label: 'ホーム', iconName: 'home' },
  { path: '/payments', label: '支出', iconName: 'wallet' },
  { path: '/budgets', label: '予算', iconName: 'chart' },
  { path: '/settlements', label: '精算', iconName: 'repeat' },
  { path: '/settings', label: '設定', iconName: 'settings' },
];

function BrandLockup() {
  return (
    <div className="brand-lockup">
      <span className="brand-mark" aria-hidden="true">
        <AppIcon name="wallet" size={18} />
      </span>
      <span>
        <span className="brand-name">シェア家計簿</span>
        <span className="brand-caption">SHARE BUDGET</span>
      </span>
    </div>
  );
}

export function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useAppStore((state) => state.toast);
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await api.getNotifications({ limit: 1, unreadOnly: true });
      setUnreadCount(res.unreadCount);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();

    const unsubscribe = setupForegroundNotificationListener(loadUnreadCount);
    const interval = setInterval(loadUnreadCount, 60000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [loadUnreadCount]);

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path));

  const isHome = location.pathname === '/';

  return (
    <div className="app-shell min-h-screen pb-28">
      {isHome && (
        <header className="app-header">
          <div className="header-inner">
            <BrandLockup />
            <button
              onClick={() => navigate('/notifications')}
              className="icon-button"
              aria-label="通知を開く"
            >
              <AppIcon name="bell" size={19} />
              {unreadCount > 0 && (
                <span className="notification-badge" aria-label={`${unreadCount}件の未読通知`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </div>
        </header>
      )}

      <main className="app-main animate-fade-in">
        <Outlet />
      </main>

      {toast && (
        <div
          className={`toast ${toast.type === 'error'
            ? 'bg-red-500'
            : toast.type === 'success'
              ? 'bg-green-500'
              : 'bg-gray-900'
            }`}
        >
          {toast.message}
        </div>
      )}

      <nav className="nav-bar" aria-label="メインナビゲーション">
        <div className="flex justify-around">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              aria-current={isActive(item.path) ? 'page' : undefined}
              aria-label={item.label}
              className={`nav-item flex-1 ${isActive(item.path) ? 'active' : ''}`}
            >
              <span className="nav-item-icon">
                <AppIcon name={item.iconName} size={20} />
              </span>
              <span className="text-[10px]">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
